import { useState } from 'react';
import { Dropdown, Image } from 'antd';
import { DeleteOutlined, EyeOutlined, FileOutlined, LinkOutlined, PaperClipOutlined, PictureOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { InlinePrimaryLink } from '@/components/ui/Link';
import { GlobalImagePreviewStyles } from '@/components/ui/GlobalImagePreviewStyles';
import { PanelSectionLabel } from '@/pages/inside/tenant/style';
import { LinkRow } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import {
    normalizePhaseLinkUrl,
    resolveAttachmentUrl,
    getAttachmentFileExtension,
    isLikelyImageAttachmentUrl,
} from '@hooks/projects/projectHelpers';

const PANEL_STYLE = {
    width: 360,
    maxWidth: 'min(360px, calc(100vw - 24px))',
    maxHeight: 320,
    overflowY: 'auto',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
    background: 'var(--ant-color-bg-elevated, #fff)',
    boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12)',
};

const GRID = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 8,
};

const cellShell = {
    aspectRatio: '1',
    minWidth: 0,
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
    textDecoration: 'none',
    color: 'inherit',
    background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
    display: 'block',
    position: 'relative',
};

const DELETE_BTN_STYLE = {
    position: 'absolute',
    right: 4,
    top: 4,
    zIndex: 4,
    width: 26,
    height: 26,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    background: 'rgba(0,0,0,0.55)',
    color: '#fff',
    opacity: 0,
    transition: 'opacity 0.15s ease',
};

function ExtensionBadge({ ext }) {
    const label = ext || 'FILE';
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 36,
                padding: '4px 8px',
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--ant-color-text-secondary, rgba(0,0,0,0.55))',
                background: 'var(--ant-color-fill-secondary, rgba(0,0,0,0.06))',
                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                fontFamily: 'var(--font-family-geist), ui-sans-serif, system-ui, sans-serif',
            }}
        >
            {label}
        </span>
    );
}

function FileGridCell({ item, onPreviewOpen, canRemove, onRemoveItem, removePending, cellSize }) {
    const href = resolveAttachmentUrl(item.url);
    const ext = getAttachmentFileExtension(item.url);
    const isImage = isLikelyImageAttachmentUrl(item.url);
    const [imgError, setImgError] = useState(false);
    const [hover, setHover] = useState(false);
    const showImage = Boolean(href && isImage && !imgError);

    const deleteBtn =
        canRemove && onRemoveItem ? (
            <button
                type="button"
                aria-label="Remove attachment"
                disabled={removePending}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!removePending) onRemoveItem(item);
                }}
                style={{
                    ...DELETE_BTN_STYLE,
                    opacity: hover && !removePending ? 1 : 0,
                    pointerEvents: hover && !removePending ? 'auto' : 'none',
                }}
            >
                <DeleteOutlined style={{ fontSize: 13 }} />
            </button>
        ) : null;

    if (showImage) {
        return (
            <div
                style={{
                    ...cellShell,
                    ...(Number.isFinite(cellSize) && cellSize > 0
                        ? { width: cellSize, height: cellSize, aspectRatio: 'auto' }
                        : {}),
                    padding: 0,
                    cursor: 'pointer',
                    overflow: canRemove && onRemoveItem ? 'visible' : 'hidden',
                }}
                aria-label={ext ? `Preview ${ext} file` : 'Preview file'}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
            >
                {deleteBtn}
                <Image
                    src={href}
                    alt={item.title || ''}
                    loading="lazy"
                    preview={{
                        mask: (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                <EyeOutlined />
                                Preview
                            </span>
                        ),
                            rootClassName: 'global-image-preview-root',
                        onVisibleChange: (visible) => {
                            if (visible && typeof onPreviewOpen === 'function') {
                                onPreviewOpen();
                            }
                        },
                    }}
                    onError={() => setImgError(true)}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                    wrapperStyle={{ width: '100%', height: '100%', display: 'block' }}
                />
            </div>
        );
    }

    return (
        <div
            style={{
                position: 'relative',
                minWidth: 0,
                ...(Number.isFinite(cellSize) && cellSize > 0 ? { width: cellSize, height: cellSize } : {}),
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {deleteBtn}
            <a
                href={href || '#'}
                target="_blank"
                rel="noopener noreferrer"
                style={cellShell}
                aria-label={ext ? `Open ${ext} file` : 'Open file'}
            >
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        padding: 8,
                        boxSizing: 'border-box',
                    }}
                >
                    <FileOutlined style={{ fontSize: 22, color: 'var(--ant-color-text-tertiary, rgba(0,0,0,0.35))' }} />
                    <ExtensionBadge ext={ext} />
                </div>
            </a>
        </div>
    );
}

function CompactFileRow({ item, canRemove, onRemoveItem, removePending }) {
    const href = resolveAttachmentUrl(item.url);
    const ext = getAttachmentFileExtension(item.url);
    const label = (item.title && String(item.title).trim()) || (href ? String(href).split('/').pop() : '') || 'File';
    const isImage = isLikelyImageAttachmentUrl(item.url);
    const [hover, setHover] = useState(false);
    const showDelete = canRemove && onRemoveItem && hover && !removePending;

    return (
        <div
            style={{ position: 'relative', minWidth: 0 }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {canRemove && onRemoveItem ? (
                <button
                    type="button"
                    aria-label="Remove attachment"
                    disabled={removePending}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!removePending) onRemoveItem(item);
                    }}
                    style={{
                        ...DELETE_BTN_STYLE,
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        opacity: showDelete ? 1 : 0,
                        pointerEvents: showDelete ? 'auto' : 'none',
                    }}
                >
                    <DeleteOutlined style={{ fontSize: 12 }} />
                </button>
            ) : null}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    borderRadius: 8,
                    background: 'transparent',
                    border: '1px solid transparent',
                    minWidth: 0,
                }}
            >
                {isImage && href ? (
                    <div style={{ width: 30, height: 30, flexShrink: 0 }}>
                        <Image
                            src={href}
                            alt={label}
                            width={30}
                            height={30}
                            style={{ objectFit: 'cover', borderRadius: 8 }}
                                    preview={{ rootClassName: 'global-image-preview-root' }}
                        />
                    </div>
                ) : null}
                <InlinePrimaryLink
                    href={href || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    inheritMarkdownSize
                    title={label}
                    style={{
                        minWidth: 0,
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 13,
                        paddingRight: canRemove && onRemoveItem ? 22 : 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                    }}
                    aria-label={ext ? `Open ${ext} file` : 'Open file'}
                >
                    {isImage ? (
                        <PictureOutlined style={{ fontSize: 12, flexShrink: 0 }} />
                    ) : (
                        <FileOutlined style={{ fontSize: 12, flexShrink: 0 }} />
                    )}
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {label}
                        {!isImage && ext ? ` (${ext})` : ''}
                    </span>
                </InlinePrimaryLink>
            </div>
        </div>
    );
}

const INLINE_ICON_ROW = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    maxWidth: '100%',
    minWidth: 0,
};

function LinkAttachmentBadge({ item, onNavigate, canRemove, onRemoveItem, removePending }) {
    const href = normalizePhaseLinkUrl(item.url);
    const label = (item.title && String(item.title).trim()) || href || 'Link';
    const hasRemove = canRemove && onRemoveItem;

    return (
        <LinkRow $hasRemove={hasRemove}>
            <a
                href={href || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onNavigate?.()}
                aria-label="Open link"
            >
                <LinkOutlined style={{ fontSize: 12, flexShrink: 0 }} />
                <span>{label}</span>
            </a>
            {hasRemove && (
                <button
                    type="button"
                    className="pending-link-remove"
                    aria-label="Remove link"
                    disabled={removePending}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!removePending) onRemoveItem(item);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <DeleteOutlined style={{ fontSize: 11 }} />
                </button>
            )}
        </LinkRow>
    );
}

/**
 * Compact trigger opens a dropdown: links and files in a 3-column grid (image preview or extension badge).
 * Optional `onRemoveItem` (e.g. milestone PATCH) shows a delete control on row hover.
 */
export default function PhaseDescriptionAttachments({
    items,
    onRemoveItem,
    removePending = false,
    cellSize,
    gridGap,
    /** When true, renders the panel content directly (no dropdown trigger). */
    inline = false,
    /** When true, hides file names and shows file tiles (extension/image). */
    hideFileNames = false,
    /** Optional override for the trigger button label. */
    triggerLabel,
    style,
    className,
}) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    if (!items?.length) return null;
    const links = items.filter((i) => i.type === 'link');
    const files = items.filter((i) => i.type !== 'link');
    const canRemove = typeof onRemoveItem === 'function';

    const closeDropdown = () => setDropdownOpen(false);
    const sizedGridStyle =
        Number.isFinite(cellSize) && cellSize > 0
            ? {
                gridTemplateColumns: `repeat(auto-fill, minmax(${cellSize}px, ${cellSize}px))`,
                justifyContent: 'flex-start',
            }
            : null;

    const panel = (
        <div style={PANEL_STYLE} onClick={closeDropdown}>
            {links.length > 0 && (
                <div style={{ marginBottom: files.length ? 14 : 0 }}>
                    <PanelSectionLabel
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 8,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            opacity: 0.75,
                        }}
                    >
                        <LinkOutlined />
                        Links
                    </PanelSectionLabel>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            alignItems: 'flex-start',
                        }}
                    >
                        {links.map((item, idx) => (
                            <LinkAttachmentBadge
                                key={`${item.url}-${idx}`}
                                item={item}
                                onNavigate={closeDropdown}
                                canRemove={canRemove}
                                onRemoveItem={onRemoveItem}
                                removePending={removePending}
                            />
                        ))}
                    </div>
                </div>
            )}
            {files.length > 0 && (
                <div>
                    <PanelSectionLabel
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            marginBottom: 8,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            opacity: 0.75,
                        }}
                    >
                        <FileOutlined />
                        Attachments
                    </PanelSectionLabel>
                    <div
                        style={{
                            ...GRID,
                            ...(sizedGridStyle || {}),
                            ...(Number.isFinite(gridGap) && gridGap >= 0 ? { gap: gridGap } : {}),
                        }}
                    >
                        {files.map((item, idx) => (
                            <FileGridCell
                                key={`${item.url}-${idx}`}
                                item={item}
                                canRemove={canRemove}
                                onRemoveItem={onRemoveItem}
                                removePending={removePending}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    if (inline) {
        const inlineImages = files.filter((i) => isLikelyImageAttachmentUrl(i.url));
        const inlineFiles = files.filter((i) => !isLikelyImageAttachmentUrl(i.url));
        return (
            <Container
                className={className}
                style={{
                    width: '100%',
                    ...(style && typeof style === 'object' ? style : {}),
                }}
            >
                <GlobalImagePreviewStyles />
                <div style={{ width: '100%', maxWidth: '100%' }}>
                    {links.length > 0 && (
                        <div style={{ marginBottom: 14 }}>
                            <PanelSectionLabel
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    marginBottom: 8,
                                    fontSize: 11,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    opacity: 0.75,
                                }}
                            >
                                <LinkOutlined />
                                Links
                            </PanelSectionLabel>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                    alignItems: 'flex-start',
                                }}
                            >
                                {links.map((item, idx) => (
                                    <LinkAttachmentBadge
                                        key={`${item.url}-${idx}`}
                                        item={item}
                                        onNavigate={undefined}
                                        canRemove={canRemove}
                                        onRemoveItem={onRemoveItem}
                                        removePending={removePending}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {(inlineImages.length > 0 || inlineFiles.length > 0) && (
                        <div>
                            <PanelSectionLabel
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    marginBottom: 8,
                                    fontSize: 11,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    opacity: 0.75,
                                }}
                            >
                                <FileOutlined />
                                Attachments
                            </PanelSectionLabel>
                            <div
                                style={{
                                    ...GRID,
                                    ...(sizedGridStyle || {}),
                                    ...(Number.isFinite(gridGap) && gridGap >= 0 ? { gap: gridGap } : {}),
                                }}
                            >
                                {[...inlineImages, ...inlineFiles].map((item, idx) => (
                                    <FileGridCell
                                        key={`${item.url}-${idx}`}
                                        item={item}
                                        onPreviewOpen={undefined}
                                        canRemove={canRemove}
                                        onRemoveItem={onRemoveItem}
                                        removePending={removePending}
                                        cellSize={cellSize}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Container>
        );
    }

    const countLabel = typeof triggerLabel === 'string' && triggerLabel.trim() ? triggerLabel.trim() : 'Attachments';

    return (
        <Container
            className={className}
            style={{
                marginTop: 10,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0,
                flexWrap: 'wrap',
                ...(style && typeof style === 'object' ? style : {}),
            }}
        >
            <GlobalImagePreviewStyles />
            <Dropdown
                open={dropdownOpen}
                onOpenChange={setDropdownOpen}
                popupRender={() => panel}
                trigger={['click']}
                placement="bottomLeft"
            >
                <button
                    type="button"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 10px',
                        fontSize: 13,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                        borderRadius: 8,
                        background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                        color: 'var(--ant-color-text, rgba(0,0,0,0.88))',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                    }}
                >
                    <PaperClipOutlined style={{ fontSize: 14 }} />
                    {countLabel} ({items.length})
                </button>
            </Dropdown>
        </Container>
    );
}
